import { TriangleAlert } from 'lucide-react';
import { Button } from '../../../components/ui';
import type { Section } from '../../../api/types';
import { personName } from '../../../lib/names';

/** One pickable audience block: a section, or the students who have none. */
export interface AudienceOption {
  /** Section id, or 0 for "No section". */
  id: number;
  /** Letter or short name shown on the tile. */
  letter: string;
  label: string;
  students: number;
  teacher: string | null;
}

export function audienceOptions(
  sections: Section[],
  unsectioned: number,
): AudienceOption[] {
  const out: AudienceOption[] = sections.map((s) => ({
    id: s.id,
    letter: s.name,
    label: s.display || s.name,
    students: s.student_count,
    teacher: s.homeroom_teacher ? personName(s.homeroom_teacher) || null : null,
  }));
  if (unsectioned > 0) out.push({ id: 0, letter: '–', label: 'No section', students: unsectioned, teacher: null });
  return out;
}

interface Props {
  gradeName: string;
  options: AudienceOption[];
  selected: Set<number>;
  onToggle: (id: number) => void;
  /** Students in the chosen sections without a roll number, with their section letter. */
  missingRoll: Array<{ name: string; section: string | null }>;
  onAssignRolls: () => void;
  loading: boolean;
}

/** Step 3: which sections sit the exam (used to count the sheets to print) and who still lacks a roll number. */
export default function SectionsStep({ gradeName, options, selected, onToggle, missingRoll, onAssignRolls, loading }: Props) {
  const shown = missingRoll.slice(0, 3);
  const more = missingRoll.length - shown.length;
  return (
    <div>
      <p className="mb-1.5 block text-[13px] font-semibold text-ink">Which sections sit this exam?</p>
      {loading ? (
        <p className="text-sm text-muted">Loading sections…</p>
      ) : options.length === 0 ? (
        <p className="text-sm text-muted">{gradeName} has no sections or students yet.</p>
      ) : (
        <div role="group" aria-label="Sections" className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-2.5">
          {options.map((o) => {
            const on = selected.has(o.id);
            return (
              <button
                key={o.id} type="button" aria-pressed={on} onClick={() => onToggle(o.id)}
                className={`flex w-full items-start gap-3 rounded-[16px] border p-4 text-left text-ink transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                  on ? 'border-ink bg-surface-2 shadow-[inset_0_0_0_1px_rgb(var(--yd-ink))]' : 'border-line bg-surface hover:border-line-hover'}`}>
                <span className={`grid h-[38px] w-[38px] flex-none place-items-center rounded-[12px] font-display text-[15px] font-bold ${on ? 'bg-accent text-navy' : 'bg-mist text-slate'}`}>
                  {o.letter}
                </span>
                <span className="min-w-0">
                  <span className="block font-semibold">{o.label}</span>
                  <span className="text-xs text-muted">
                    {o.students} {o.students === 1 ? 'student' : 'students'}{o.teacher ? ` · ${o.teacher}` : ''}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
      <p className="mt-2 text-[13px] text-muted">
        Sections only decide how many sheets to print. Any student in {gradeName} can be matched to a sheet by roll number.
      </p>

      {missingRoll.length > 0 && (
        <div className="mt-[18px] flex flex-wrap items-center gap-3 rounded-[14px] bg-accent-bg p-3.5">
          <TriangleAlert size={18} strokeWidth={1.8} aria-hidden className="flex-none text-accent-ink" />
          <span className="min-w-[260px] flex-1 text-warm-ink">
            <strong>{missingRoll.length} {missingRoll.length === 1 ? 'student has' : 'students have'} no roll number.</strong>{' '}
            Their sheets can&apos;t be matched automatically: {shown.map((m) => (m.section ? `${m.name} (${m.section})` : m.name)).join(', ')}
            {more > 0 ? ` and ${more} more` : ''}.
          </span>
          <Button variant="ghost" size="sm" onClick={onAssignRolls}>Assign roll numbers</Button>
        </div>
      )}
    </div>
  );
}
